import {
    AdMob,
    BannerAdSize,
    BannerAdPosition,
    BannerAdPluginEvents,
    InterstitialAdPluginEvents,
} from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';

const SDK_START_DELAY_MS = 1200;
const BANNER_LOAD_TIMEOUT_MS = 15000;
const INTERSTITIAL_RETRY_MS = 8000;
const BANNER_RETRY_MS = 8000;
const MAX_RETRIES = 4;

class AdMobService {
    constructor() {
        this.isWeb = Capacitor.getPlatform() === 'web';
        this.isInitialized = false;
        this.isInitializing = false;
        this.isBannerVisible = false;
        this.isBannerLoading = false;
        this.isInterstitialReady = false;
        this.isPreparingInterstitial = false;
        this.interstitialListenersAttached = false;
        this.bannerRetries = 0;
        this.interstitialRetries = 0;
        this.bannerRetryTimer = null;
        this.interstitialRetryTimer = null;
        this.onBannerHeight = null;

        this.bannerAdIds = {
            ios: 'ca-app-pub-7803359681700997/2109968913',
            android: 'ca-app-pub-3940256099942544/6300978111',
        };

        this.interstitialAdIds = {
            ios: 'ca-app-pub-7803359681700997/4509975621',
            android: 'ca-app-pub-3940256099942544/1033173712',
        };
    }

    getBannerAdId() {
        return Capacitor.getPlatform() === 'ios'
            ? this.bannerAdIds.ios
            : this.bannerAdIds.android;
    }

    getInterstitialAdId() {
        return Capacitor.getPlatform() === 'ios'
            ? this.interstitialAdIds.ios
            : this.interstitialAdIds.android;
    }

    setBannerHeightListener(callback) {
        this.onBannerHeight = typeof callback === 'function' ? callback : null;
    }

    notifyBannerHeight(height) {
        if (this.onBannerHeight) this.onBannerHeight(height);
    }

    async initialize() {
        if (this.isWeb) return;
        if (this.isInitialized) return;
        if (this.isInitializing) {
            while (this.isInitializing) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            return;
        }

        this.isInitializing = true;

        try {
            await AdMob.initialize({
                initializeForTesting: false,
            });

            // Plugin resolves before MobileAds.start finishes.
            await new Promise((resolve) => setTimeout(resolve, SDK_START_DELAY_MS));

            await this.gatherConsent();

            this.isInitialized = true;
            await this.attachInterstitialListeners();
            this.prepareInterstitial();
        } catch (error) {
            console.error('AdMob initialization error:', error);
        } finally {
            this.isInitializing = false;
        }
    }

    async gatherConsent() {
        try {
            const tracking = await AdMob.trackingAuthorizationStatus();
            if (tracking?.status === 'notDetermined') {
                await AdMob.requestTrackingAuthorization();
            }
        } catch (error) {
            console.warn('ATT request skipped:', error);
        }

        try {
            const consentInfo = await AdMob.requestConsentInfo();
            if (consentInfo?.isConsentFormAvailable && consentInfo.status === 'REQUIRED') {
                await AdMob.showConsentForm();
            }
        } catch (error) {
            console.warn('UMP consent skipped:', error);
        }
    }

    async attachInterstitialListeners() {
        if (this.isWeb || this.interstitialListenersAttached) return;

        await AdMob.addListener(InterstitialAdPluginEvents.Loaded, () => {
            this.isInterstitialReady = true;
            this.isPreparingInterstitial = false;
            this.interstitialRetries = 0;
        });

        await AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, (error) => {
            console.warn('Interstitial failed to load:', error);
            this.isInterstitialReady = false;
            this.isPreparingInterstitial = false;
            this.scheduleInterstitialRetry();
        });

        await AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
            this.isInterstitialReady = false;
            this.prepareInterstitial();
        });

        await AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, (error) => {
            console.warn('Interstitial failed to show:', error);
            this.isInterstitialReady = false;
            this.prepareInterstitial();
        });

        this.interstitialListenersAttached = true;
    }

    scheduleInterstitialRetry() {
        if (this.interstitialRetries >= MAX_RETRIES) return;
        if (this.interstitialRetryTimer) return;
        this.interstitialRetryTimer = setTimeout(() => {
            this.interstitialRetryTimer = null;
            this.interstitialRetries += 1;
            this.prepareInterstitial();
        }, INTERSTITIAL_RETRY_MS);
    }

    scheduleBannerRetry() {
        if (this.bannerRetries >= MAX_RETRIES) return;
        if (this.bannerRetryTimer) return;
        this.bannerRetryTimer = setTimeout(() => {
            this.bannerRetryTimer = null;
            this.bannerRetries += 1;
            this.showBanner();
        }, BANNER_RETRY_MS);
    }

    async prepareInterstitial() {
        if (this.isWeb || !this.isInitialized) return;
        if (this.isPreparingInterstitial || this.isInterstitialReady) return;

        this.isPreparingInterstitial = true;
        this.isInterstitialReady = false;

        try {
            // Keep isTesting false so the plugin does not swap in Android sample IDs.
            await AdMob.prepareInterstitial({
                adId: this.getInterstitialAdId(),
                isTesting: false,
            });
        } catch (error) {
            console.warn('prepareInterstitial error:', error);
            this.isPreparingInterstitial = false;
            this.isInterstitialReady = false;
        }
    }

    async showInterstitialIfReady() {
        if (this.isWeb || !this.isInitialized || !this.isInterstitialReady) {
            if (!this.isWeb && this.isInitialized && !this.isPreparingInterstitial) {
                this.prepareInterstitial();
            }
            return false;
        }

        try {
            this.isInterstitialReady = false;
            await AdMob.showInterstitial();
            return true;
        } catch (error) {
            console.warn('showInterstitial error:', error);
            this.isInterstitialReady = false;
            this.prepareInterstitial();
            return false;
        }
    }

    async showBanner() {
        if (this.isWeb || this.isBannerVisible || this.isBannerLoading) return 0;
        if (!this.isInitialized) {
            await this.initialize();
        }
        if (!this.isInitialized) return 0;

        this.isBannerLoading = true;

        try {
            const adId = this.getBannerAdId();

            return await new Promise((resolve) => {
                let loadedListener;
                let failedListener;
                let sizeListener;
                let settled = false;

                const timeout = setTimeout(() => {
                    cleanup(this.isBannerVisible ? this.getBannerHeight() : 0);
                }, BANNER_LOAD_TIMEOUT_MS);

                const cleanup = (result) => {
                    if (settled) return;
                    settled = true;
                    this.isBannerLoading = false;
                    clearTimeout(timeout);
                    if (loadedListener) loadedListener.remove();
                    if (failedListener) failedListener.remove();
                    if (sizeListener) sizeListener.remove();
                    if (result > 0) this.notifyBannerHeight(result);
                    else this.scheduleBannerRetry();
                    resolve(result);
                };

                Promise.all([
                    AdMob.addListener(BannerAdPluginEvents.Loaded, () => {
                        this.isBannerVisible = true;
                        this.bannerRetries = 0;
                        cleanup(this.getBannerHeight());
                    }),
                    AdMob.addListener(BannerAdPluginEvents.FailedToLoad, (error) => {
                        console.warn('Banner ad failed to load:', error);
                        this.isBannerVisible = false;
                        cleanup(0);
                    }),
                    AdMob.addListener(BannerAdPluginEvents.SizeChanged, (info) => {
                        if (info?.height > 0) {
                            this.isBannerVisible = true;
                            this.bannerRetries = 0;
                            cleanup(Math.round(info.height));
                        }
                    }),
                ]).then(([loaded, failed, size]) => {
                    loadedListener = loaded;
                    failedListener = failed;
                    sizeListener = size;

                    AdMob.showBanner({
                        adId,
                        adSize: BannerAdSize.BANNER,
                        position: BannerAdPosition.TOP_CENTER,
                        margin: 0,
                        isTesting: false,
                    }).catch((err) => {
                        console.error('AdMob.showBanner call failed:', err);
                        cleanup(0);
                    });
                });
            });
        } catch (error) {
            console.error('showBanner error:', error);
            this.isBannerLoading = false;
            this.scheduleBannerRetry();
            return 0;
        }
    }

    async hideBanner() {
        if (this.isWeb || !this.isBannerVisible) return;

        try {
            await AdMob.hideBanner();
            this.isBannerVisible = false;
        } catch (error) {
            console.error('Error hiding banner:', error);
        }
    }

    async removeBanner() {
        if (this.isWeb) return;

        try {
            await AdMob.removeBanner();
            this.isBannerVisible = false;
            this.isBannerLoading = false;
        } catch (error) {
            console.error('Error removing banner:', error);
        }
    }

    getBannerHeight() {
        return 50;
    }

    async resumeBanner() {
        if (this.isWeb) return;
        try {
            await AdMob.resumeBanner();
        } catch (error) {
            console.error('Error resuming banner:', error);
        }
    }

    async removeAllListeners() {
        if (this.isWeb) return;
        this.interstitialListenersAttached = false;
        this.isInterstitialReady = false;
        this.isPreparingInterstitial = false;
        this.isBannerLoading = false;
        if (this.bannerRetryTimer) {
            clearTimeout(this.bannerRetryTimer);
            this.bannerRetryTimer = null;
        }
        if (this.interstitialRetryTimer) {
            clearTimeout(this.interstitialRetryTimer);
            this.interstitialRetryTimer = null;
        }
        await AdMob.removeAllListeners();
    }
}

export default new AdMobService();
